// src/api/api.js
import axios from "axios";
import { API_TIMEOUTS, GET_RETRY_DELAYS } from "./apiConfig";
import { ACTING_CITY_HEADER, actingCityHeaderValue } from "../utils/actingCity";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "https://elitlig-api-88a866b7a4da.herokuapp.com/",
  withCredentials: true,
  timeout: API_TIMEOUTS.DEFAULT,
});

const DEFAULT_GET_CACHE_TTL = 0;
const inflightGetRequests = new Map();
const responseCache = new Map();
const MAX_GET_CACHE_ENTRIES = 100;

const buildGetKey = (config = {}) => {
  const base = config.baseURL || api.defaults.baseURL || "";
  const url = config.url || "";
  const params = config.params || {};
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      acc[key] = params[key];
      return acc;
    }, {});
  return `GET::${base}::${url}::${JSON.stringify(sortedParams)}`;
};

const debugEnabled =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_API_DEBUG === "true";

const requestId = () =>
  (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
  `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new axios.CanceledError("Request aborted"));
    }, { once: true });
  });

// Zaman aşımına uğrayan istek yeniden denenmez: 15 sn'lik bir bekleme iki kez
// daha tekrarlanınca tek istek 45 sn boyunca tarayıcının bağlantı kotasını
// tutuyor ve sayfadaki diğer istekleri kuyruğa sokuyordu. Yalnızca ağ hatası
// ve geçici sunucu yanıtları (429/502/503/504) yeniden denenir.
const TIMEOUT_CODES = new Set(["ECONNABORTED", "ETIMEDOUT"]);
const MAX_RETRY_AFTER_MS = 8000;

const retryAfterMs = (error) => {
  const header = Number(error?.response?.headers?.["retry-after"]);
  return Number.isFinite(header) && header > 0 ? header * 1000 : 0;
};

// Veritabanı saatlik sorgu kotası dolduğunda sunucu 503 + DB_QUOTA döner ve
// kota en geç saat başında açılır; birkaç saniye sonra yeniden denemek yalnızca
// yükü katlar. Retry-After 8 sn'den uzunsa da beklenmez: istek hemen başarısız
// olur, sayfa kendi hata/son veri görünümüne düşer.
const isLongOutage = (error) => {
  const status = error?.response?.status;
  if (status !== 503 && status !== 429) return false;
  const data = error?.response?.data;
  if (/^DB_QUOTA/.test(String(data?.code || data?.errorCode || ""))) return true;
  return retryAfterMs(error) > MAX_RETRY_AFTER_MS;
};

const isRetryableGetError = (error) => {
  const status = error?.response?.status;
  if (error?.config?.signal?.aborted || TIMEOUT_CODES.has(error?.code)) return false;
  if (isLongOutage(error)) return false;
  return !status || [429, 502, 503, 504].includes(status);
};

// Sunucu yoğunken (503/429) tüm ziyaretçilerin aynı anda geri dönmesi yükü
// katlıyordu: sunucunun Retry-After önerisine uyulur (en çok 8 sn) ve bekleme
// rastgele dağıtılır.
const retryDelay = (error, retryCount) => {
  const base = GET_RETRY_DELAYS[retryCount] || 0;
  const suggested = Math.min(retryAfterMs(error), MAX_RETRY_AFTER_MS);
  const delay = Math.max(base, suggested);
  return Math.round(delay * (0.75 + Math.random() * 0.5));
};

const performGet = async (url, config) => {
  let retryCount = 0;
  while (true) {
    try {
      const response = await api.get(url, config);
      response.config.__retryCount = retryCount;
      return response;
    } catch (error) {
      if (!isRetryableGetError(error) || retryCount >= GET_RETRY_DELAYS.length) throw error;
      await wait(retryDelay(error, retryCount), config.signal);
      retryCount += 1;
    }
  }
};

export const apiGet = (url, config = {}) => {
  const key = buildGetKey({ ...config, url });
  const cacheTtl = Number(config.cacheTtl ?? DEFAULT_GET_CACHE_TTL);
  const dedupe = config.dedupe !== false;
  const useCache = cacheTtl > 0 && config.useCache !== false;

  if (useCache && responseCache.has(key)) {
    const cached = responseCache.get(key);
    if (cached.expiresAt > Date.now()) {
      if (debugEnabled) console.debug("[api]", { method: "GET", url, cacheHit: true });
      return Promise.resolve(cached.response);
    }
    responseCache.delete(key);
  }

  if (dedupe && inflightGetRequests.has(key)) {
    if (debugEnabled) console.debug("[api]", { method: "GET", url, deduped: true });
    return inflightGetRequests.get(key);
  }

  const { cacheTtl: _cacheTtl, dedupe: _dedupe, useCache: _useCache, ...axiosConfig } = config;
  const request = performGet(url, axiosConfig).then((response) => {
    if (useCache) {
      responseCache.set(key, {
        expiresAt: Date.now() + cacheTtl,
        response,
      });
      while (responseCache.size > MAX_GET_CACHE_ENTRIES) {
        responseCache.delete(responseCache.keys().next().value);
      }
    }
    return response;
  }).finally(() => {
    inflightGetRequests.delete(key);
  });

  inflightGetRequests.set(key, request);
  return request;
};

export const clearApiGetCache = (matcher) => {
  if (!matcher) {
    responseCache.clear();
    return;
  }
  Array.from(responseCache.keys()).forEach((key) => {
    if (matcher(key)) responseCache.delete(key);
  });
};

export const invalidateApiCache = (urlPart) =>
  clearApiGetCache((key) => key.includes(urlPart));

// Test ve geliştirme ölçümleri için; uygulama kodu doğrudan kullanmamalı.
export const __resetApiRequestState = () => {
  inflightGetRequests.clear();
  responseCache.clear();
};

export const AUTH_TOKEN_KEY = "accessToken";

// Oturum çerezi (httpOnly) birinci kaynaktır. Ancak site ile API farklı alan
// adlarında olduğu için çerez üçüncü taraf sayılır; Safari ITP ve Chrome'un
// çerez kısıtları bunu düşürebiliyor. Bu durumda giriş yanıtındaki jeton burada
// saklanır ve her isteğe Authorization başlığı olarak eklenir — aksi halde panel
// açık görünürken ilk API çağrısı "Giriş yapmanız gerekiyor" ile 401 dönüyordu.
export const setAuthToken = (token) => {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  else clearAuthToken();
};

export const clearAuthToken = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem("token");
};

const isForeignUrl = (config) => {
  const url = String(config?.url || "");
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    return new URL(url).origin !== new URL(config.baseURL || api.defaults.baseURL).origin;
  } catch {
    return true;
  }
};

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  // Cookie sessions remain enabled, while installations which issue a bearer
  // token use the very same client (including the final-prediction admin API).
  if (!config.headers.Authorization && typeof window !== "undefined") {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem("token");
    let userToken = "";
    try { userToken = JSON.parse(localStorage.getItem("user") || "null")?.token || ""; } catch { /* malformed cache is ignored */ }
    const token = storedToken || userToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  // Türkiye geneli kullanıcı il seçtiyse sunucu bu isteği o ilin kapsamında işler.
  // Mutlak adresle başka bir sunucuya giden isteğe eklenmez (CORS ön kontrolü).
  if (!config.headers[ACTING_CITY_HEADER] && !isForeignUrl(config)) {
    const actingCity = actingCityHeaderValue();
    if (actingCity) config.headers[ACTING_CITY_HEADER] = actingCity;
  }
  if (!config.headers["X-Request-ID"]) config.headers["X-Request-ID"] = requestId();
  config.__startedAt = Date.now();
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (debugEnabled) {
      console.debug("[api]", {
        method: response.config?.method?.toUpperCase(),
        endpoint: response.config?.url,
        duration: Date.now() - (response.config?.__startedAt || Date.now()),
        retryCount: response.config?.__retryCount || 0,
        reason: response.config?.__requestReason,
      });
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const currentPath = window.location.pathname || "";

    // Editor/Admin alanında session bitince yönetim girişine at. Üye alanında
    // (panel) oturum düşerse üye giriş sayfası doğru hedeftir.
    if (
      status === 401 &&
      (currentPath.startsWith("/editor") || currentPath.startsWith("/yonetim")) &&
      currentPath !== "/yonetim/giris"
    ) {
      alert("Oturumunuz sona erdi. Lütfen tekrar giriş yapın.");
      window.location.href = "/yonetim/giris";
    }

    return Promise.reject(error);
  }
);

export default api;
